"use client"

import React from 'react'
import { Mic, RotateCcw, Square, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { splitDuration } from '@/lib/voice/format-duration'
import { VoiceEqualizer } from './voice-equalizer'
import { useVoiceInput, type VoiceInputApi, type VoiceInputState } from './use-voice-input'

export type { VoiceInputState }

export interface VoiceInputButtonProps {
  /** Вставить распознанный текст (обязателен) */
  onInsert: (text: string) => void
  onStateChange?: (state: VoiceInputState) => void
  disabled?: boolean
  /** sm — для Input (28px), md — для Textarea/копилота (32px) */
  size?: 'sm' | 'md'
  /**
   * inline — пилюля растёт влево от кнопки внутри потока (Textarea, копилот).
   * overlay — в recording/transcribing/error пилюля рендерится поверх всего поля
   *           (`absolute inset-0`, родитель должен быть `relative`) — для узких Input.
   */
  placement?: 'inline' | 'overlay'
  /** Классы idle-кнопки (позиционирование внутри поля) */
  className?: string
  /** Классы иконки микрофона idle-кнопки */
  iconClassName?: string
  /** Классы оверлея (радиус/фон поля) для placement="overlay" */
  overlayClassName?: string
  /** Заголовок-подсказка idle-кнопки */
  title?: string
  /**
   * Вид idle-кнопки: `filled` — круглая с серым фоном (копилот, textarea, mobile);
   * `ghost` — только иконка, темнеет при наведении (узкие однострочные Input на desktop)
   */
  variant?: 'filled' | 'ghost'
  maxDurationSec?: number
  /** Управление записью снаружи (например, «Отправить» во время диктовки останавливает запись) */
  controlRef?: React.Ref<VoiceInputApi>
}

const STATUS_TEXT: Record<VoiceInputState, string> = {
  idle: '',
  recording: 'Идёт запись',
  transcribing: 'Распознавание речи',
  error: 'Ошибка голосового ввода',
}

// Не отдаём фокус кнопке — поле сохраняет фокус и позицию курсора для вставки
const keepFocus = (e: React.MouseEvent) => e.preventDefault()

export function VoiceInputButton({
  onInsert,
  onStateChange,
  disabled,
  size = 'sm',
  placement = 'inline',
  className,
  iconClassName,
  overlayClassName,
  title = 'Голосовой ввод',
  variant = 'filled',
  maxDurationSec,
  controlRef,
}: VoiceInputButtonProps) {
  const voice = useVoiceInput({ onInsert, onStateChange, maxDurationSec })
  React.useImperativeHandle(controlRef, () => voice)
  const { state, errorMessage, elapsedSec, getLevel } = voice

  const sm = size === 'sm'
  const dim = sm ? 'h-7' : 'h-8'
  const iconCls = sm ? 'h-3.5 w-3.5' : 'h-4 w-4'
  const isOverlay = placement === 'overlay'

  const liveRegion = (
    <span className="sr-only" aria-live="polite">
      {state === 'error' && errorMessage ? `${STATUS_TEXT.error}: ${errorMessage}` : STATUS_TEXT[state]}
    </span>
  )

  if (state === 'idle') {
    return (
      <>
        <button
          type="button"
          disabled={disabled}
          onMouseDown={keepFocus}
          onClick={(e) => { e.stopPropagation(); voice.startRecording() }}
          aria-label={title}
          title={title}
          data-voice-button
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors',
            'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:pointer-events-none disabled:opacity-50',
            variant === 'ghost'
              ? 'h-6 w-6 bg-transparent'
              : cn('bg-muted hover:bg-muted-foreground/20', dim, sm ? 'w-7' : 'w-8'),
            className,
          )}
        >
          <Mic className={cn(variant === 'ghost' ? 'h-4 w-4' : iconCls, iconClassName)} />
        </button>
        {liveRegion}
      </>
    )
  }

  const pillBase = cn(
    'flex items-center gap-2 whitespace-nowrap select-none text-xs tabular-nums',
    isOverlay
      ? 'absolute inset-0 z-10 h-full w-full rounded-md border px-2'
      : cn('shrink-0 rounded-full px-1.5', dim),
  )

  let content: React.ReactNode
  let tone: string

  if (state === 'recording') {
    const { head, seconds } = splitDuration(elapsedSec)
    tone = isOverlay ? 'bg-white dark:bg-input/30 border-input text-foreground' : 'bg-muted text-foreground'
    content = (
      <button
        type="button"
        onMouseDown={keepFocus}
        onClick={(e) => { e.stopPropagation(); voice.stopAndTranscribe() }}
        aria-label="Остановить запись и распознать"
        title="Остановить запись и распознать"
        className={cn('flex h-full w-full items-center gap-2 rounded-full focus-visible:outline-none', isOverlay ? 'justify-between' : '')}
      >
        <span className={cn('flex shrink-0 items-center justify-center rounded-full bg-red-500 text-white', sm ? 'h-5 w-5' : 'h-6 w-6')}>
          <Square className={cn(sm ? 'h-2.5 w-2.5' : 'h-3 w-3', 'fill-current')} />
        </span>
        <span className="flex items-baseline font-medium">
          <span>{head}</span>
          <span>:</span>
          <span className="w-[2ch] text-left">{seconds}</span>
        </span>
        <VoiceEqualizer getLevel={getLevel} className={cn('text-foreground/70', isOverlay ? 'ml-auto' : '')} />
      </button>
    )
  } else if (state === 'transcribing') {
    tone = isOverlay ? 'bg-white dark:bg-input/30 border-input text-muted-foreground' : 'bg-muted text-muted-foreground'
    content = (
      <>
        <span className="flex items-center gap-1 px-1" aria-hidden="true">
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
        </span>
        <span className={cn(isOverlay ? '' : 'hidden sm:inline')}>Распознаём…</span>
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={(e) => { e.stopPropagation(); voice.skip() }}
          aria-label="Отменить распознавание"
          title="Отменить распознавание"
          className={cn('flex shrink-0 items-center justify-center rounded-full hover:bg-foreground/10 focus-visible:outline-none', sm ? 'h-5 w-5' : 'h-6 w-6', isOverlay ? 'ml-auto' : '')}
        >
          <X className={iconCls} />
        </button>
      </>
    )
  } else {
    tone = 'bg-red-50 border border-red-300 text-red-600 dark:bg-red-950/40 dark:border-red-800 dark:text-red-400'
    content = (
      <>
        {/* Текст ошибки — только в title/aria-live, в пилюле короткое «Ошибка» */}
        <span className={cn('truncate font-medium', isOverlay ? 'flex-1' : '')} title={errorMessage ?? undefined}>
          Ошибка
        </span>
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={(e) => { e.stopPropagation(); voice.retry() }}
          aria-label="Повторить"
          title="Повторить"
          className={cn('flex shrink-0 items-center justify-center rounded-full hover:bg-red-600/10 focus-visible:outline-none', sm ? 'h-5 w-5' : 'h-6 w-6', isOverlay ? 'ml-auto' : '')}
        >
          <RotateCcw className={iconCls} />
        </button>
        <button
          type="button"
          onMouseDown={keepFocus}
          onClick={(e) => { e.stopPropagation(); voice.dismissError() }}
          aria-label="Отмена"
          title="Отмена"
          className={cn('flex shrink-0 items-center justify-center rounded-full hover:bg-red-600/10 focus-visible:outline-none', sm ? 'h-5 w-5' : 'h-6 w-6')}
        >
          <X className={iconCls} />
        </button>
      </>
    )
  }

  return (
    <>
      <div
        data-voice-state={state}
        className={cn(
          pillBase,
          // Классы оверлея повторяют вид поля (радиус/фон); в error красная тональность приоритетнее
          isOverlay ? (state === 'error' ? cn(overlayClassName, tone) : cn(tone, overlayClassName)) : tone,
          !isOverlay && state === 'error' && 'border',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
      {liveRegion}
    </>
  )
}
